/**
 * Session persistence shared by every login path.
 *
 * The backend returns an identical payload for password login and SSO, so the
 * storage side must be identical too. Anything that reads sessionStorage after
 * login (getUserFlags, the API interceptor, the Risk Portal role checks) can
 * then stay unaware of how the user actually signed in.
 *
 * SignIn.js still has its own inline copy of this logic. Migrating it here is
 * a separate change -- see changelog.
 */
import { alert_chat_initialize, getUserProfileService } from '../../services';

/**
 * Write the login payload to storage exactly as SignIn.js does.
 * @param {object} data  response_data.data from login or SSO exchange
 * @param {string} email address to remember for the profile screen
 */
export function persistLoginSession(data, email) {
  if (!data?.access_token) {
    throw new Error('Login response contained no access token.');
  }

  sessionStorage.setItem('access_token', data.access_token);
  sessionStorage.setItem('refresh_token', data.refresh_token);
  sessionStorage.setItem('organization_id', data.organization_id);
  localStorage.setItem('organization_id', data.organization_id);
  sessionStorage.setItem('is_otp_allowed', '/');

  if (email) {
    localStorage.setItem('email', email);
  }

  if (data.risk_role) {
    localStorage.setItem('risk_role', data.risk_role);
  } else {
    localStorage.removeItem('risk_role');
  }

  // Consumed by getUserFlags() throughout the app.
  if (data.flags) {
    sessionStorage.setItem('user_flags', JSON.stringify(data.flags));
  }
}

/**
 * Best-effort steps that follow a login. Neither should be able to strand a
 * user who is already authenticated, so both failures are swallowed.
 * @returns {Promise<object|null>} the profile, when it could be fetched
 */
export async function completeLoginSideEffects(organizationId) {
  try {
    await alert_chat_initialize({ organization_id: organizationId });
  } catch (error) {
    console.error('Error initializing alert chat:', error);
  }

  try {
    const profileResponse = await getUserProfileService();
    const profile = profileResponse?.statusCode?.response_data?.data;
    if (profile) {
      localStorage.setItem('user_profile', JSON.stringify(profile));
      return profile;
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
  }
  return null;
}

/** Same required-fields rule SignIn.js applies before showing the profile modal. */
export function isProfileIncomplete(profile) {
  if (!profile) return true;
  const requiredFields = [
    'industry_group', 'region', 'primary_regulatory_profile', 'customer_size',
  ];
  return requiredFields.some(
    (field) => !profile?.organization?.[field] || profile?.organization?.[field] === ''
  );
}

/**
 * Did this session begin at an identity provider?
 *
 * Read from the access token's `auth` claim rather than a flag we write at
 * login. The token is issued by whichever path authenticated the user, so it
 * cannot disagree with itself, and nothing has to be cleared when a user signs
 * in a second time by a different route. Anything unreadable counts as a
 * password session, which is the behaviour that existed before single logout.
 */
export function isSsoSession() {
  const token = sessionStorage.getItem('access_token');
  if (!token) return false;

  try {
    const [, payload] = token.split('.');
    if (!payload) return false;
    // Base64url, and JWTs drop the padding that atob() wants back.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded))?.auth === 'sso';
  } catch (error) {
    return false;
  }
}

/** Where to land after login, honouring a deep link the user was bounced from. */
export function consumeRedirectTarget() {
  const redirectTo = sessionStorage.getItem('redirect_after_login') || '/product-dashboard';
  sessionStorage.removeItem('redirect_after_login');
  return redirectTo;
}
